import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Button, TextField, Modal, Backdrop, Fade, Paper, IconButton, Alert, Snackbar, CircularProgress,
    FormControl, InputLabel, Select, MenuItem, Grid,
    Chip, useTheme, useMediaQuery
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BusinessIcon from '@mui/icons-material/Business';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ClientTable from './ClientTable';
import { addClient, updateClient, deleteClient, getAllClients, addProfileImage } from '../Queries';
import DraftEditor from '../Assessments/DraftEditor';

const ClientManagement = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const currentUser = localStorage.getItem("user");

    const [clients, setClients] = useState([]);
    const [loadTableData, setLoadTableData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [formData, setFormData] = useState({
        client_id: '',
        client_code: '',
        client_logo: '',
        description: '',
        status: 'Active',
        page_privileges: [],
        created_by: currentUser || ''
    });
    const [alert, setAlert] = useState({
        open: false,
        severity: 'success',
        message: ''
    });
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState('');

    // Available routes/menus for page privileges (matching actual route names)
    const availablePages = [
        { value: 'Dashboard', label: 'Dashboard' },
        { value: 'Assessments', label: 'Assessments' },
        { value: 'User Management', label: 'User Management' },
        { value: 'Data Management', label: 'Data Management' },
        { value: 'LinkedIn Feed', label: 'LinkedIn Feed' },
        { value: 'Minute Minder', label: 'Minute Minder' },
        { value: 'Self-Reflect & Rise', label: 'Self-Reflect & Rise' },
        { value: 'Growth Conversations', label: 'Growth Conversations' }
    ];

    useEffect(() => {
        loadClients();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Update table data when clients change
    useEffect(() => {
        if (clients) {
            const tableData = clients.map((client, index) => ({
                client_id: client.client_id, // Use actual client ID from API
                row_id: index + 1, // Use this for display row numbers if needed
                client_code: client.client_code,
                client_logo: client.client_logo,
                description: client.description,
                status: client.status,
                page_privileges: client.page_privileges || [],
                created_by: client.created_by,
                created_at: client.created_at,
                updated_at: client.updated_at
            }));
            setLoadTableData(tableData);
        }
    }, [clients]);


    const loadClients = async () => {
        setLoading(true);
        try {
            const response = await getAllClients();
            setClients(response || []);
        } catch (error) {
            console.error('Error loading clients:', error);
            showAlert('error', 'Failed to load clients');
        }
        setLoading(false);
    };

    const showAlert = (severity, message) => {
        setAlert({
            open: true,
            severity,
            message
        });
    };

    const handleCloseAlert = () => {
        setAlert(prev => ({ ...prev, open: false }));
    };

    const handleOpenModal = (client = null) => {
        if (client) {
            console.log('[ClientManagement] Opening edit modal for client:', client);
            console.log('[ClientManagement] Using client ID:', client.client_id);
            setFormData({
                client_id: client.client_id,
                client_code: client.client_code || '',
                client_logo: client.client_logo || '',
                description: client.description || '',
                status: client.status || 'Active',
                page_privileges: client.page_privileges || [],
                created_by: client.created_by || currentUser || ''
            });
            setImagePreview(client.client_logo || '');
            setEditMode(true);
        } else {
            setFormData({
                client_id: '',
                client_code: '',
                client_logo: '',
                description: '',
                status: 'Active',
                page_privileges: [],
                created_by: currentUser || ''
            });
            setImagePreview('');
            setEditMode(false);
        }
        setDialogOpen(true);
    };

    const handleCloseModal = () => {
        setDialogOpen(false);
        setImageFile(null);
        setImagePreview('');
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleImageChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setImageFile(file);
            const reader = new FileReader();
            reader.onload = (e) => {
                setImagePreview(e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handlePagePrivilegeToggle = (pageValue) => {
        setFormData(prev => ({
            ...prev,
            page_privileges: prev.page_privileges.includes(pageValue)
                ? prev.page_privileges.filter(p => p !== pageValue)
                : [...prev.page_privileges, pageValue]
        }));
    };

    const handleSubmit = async () => {
        if (!formData.client_code) {
            showAlert('error', 'Client code is required');
            return;
        }

        setLoading(true);
        try {
            let logoUrl = formData.client_logo;

            // Upload image if a new file is selected
            if (imageFile) {
                const fileReader = new FileReader();
                fileReader.readAsDataURL(imageFile);
                
                await new Promise((resolve, reject) => {
                    fileReader.onload = async () => {
                        try {
                            const imageData = fileReader.result.split(',')[1];
                            const fileName = `client_${formData.client_code || Date.now()}.jpg`;
                            const imageResponse = await addProfileImage(fileName, imageData, 'client_logos');
                            if (imageResponse && imageResponse.data && imageResponse.data.statusCode === 200) {
                                const result = JSON.parse(imageResponse.data.body);
                                logoUrl = result.imageUrl;
                            }
                            resolve();
                        } catch (error) {
                            reject(error);
                        }
                    };
                    fileReader.onerror = reject;
                });
            }

            const clientData = {
                ...formData,
                client_logo: logoUrl
            };

            console.log('[ClientManagement] Submitting client data:', clientData);
            console.log('[ClientManagement] Edit mode:', editMode);

            if (editMode) {
                console.log('[ClientManagement] Updating client with ID:', clientData.client_id);
                await updateClient(clientData);
                showAlert('success', 'Client updated successfully');
            } else {
                await addClient(clientData);
                showAlert('success', 'Client added successfully');
            }

            loadClients();
            handleCloseModal();
        } catch (error) {
            console.error('Error saving client:', error);
            showAlert('error', 'Failed to save client');
        }
        setLoading(false);
    };

    const handleDelete = async (clientId, clientCode) => {
        if (window.confirm('Are you sure you want to delete this client?')) {
            setLoading(true);
            try {
                await deleteClient(clientId, clientCode);
                showAlert('success', 'Client deleted successfully');
                loadClients();
            } catch (error) {
                console.error('Error deleting client:', error);
                showAlert('error', 'Failed to delete client');
            }
            setLoading(false);
        }
    };


    return (
        <Box sx={{
            bgcolor: 'white',
            minHeight: '100vh'
        }}>
            {/* Client Table */}
            <ClientTable
                tableData={loadTableData}
                handleEdit={handleOpenModal}
                handleDelete={handleDelete}
                isLoading={loading}
                actionButtons={
                    <Button
                        variant="contained"
                        onClick={() => handleOpenModal()}
                        startIcon={<AddIcon sx={{ fontSize: '1rem' }} />}
                        size="small"
                        sx={{
                            fontSize: "0.75rem",
                            textTransform: "capitalize",
                            background: 'linear-gradient(135deg, #0a0068 0%, #cc4049 100%)',
                            color: "#ffffff",
                            borderRadius: 1.5,
                            px: 1.5,
                            py: 0.75,
                            minHeight: '36px',
                            "&:hover": {
                                background: 'linear-gradient(135deg, #0c0082 0%, #d63550 100%)',
                                transform: 'translateY(-1px)'
                            },
                            transition: 'all 0.2s ease-in-out',
                        }}
                    >
                        Add Client
                    </Button>
                }
            />

            {/* Enhanced Modal */}
            <Modal
                open={dialogOpen}
                onClose={handleCloseModal}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                }}
            >
                <Fade in={dialogOpen}>
                    <Paper
                        sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            width: isMobile ? "95%" : "70%",
                            maxWidth: 700,
                            maxHeight: "85vh",
                            borderRadius: 3,
                            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                            outline: 'none',
                        }}
                    >
                        {/* Enhanced Modal Header */}
                        <Box
                            sx={{
                                p: 2.5,
                                background: 'linear-gradient(135deg, #0a0068 0%, #cc4049 100%)',
                                color: 'white',
                                borderRadius: '8px 8px 0 0',
                                position: 'relative',
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    height: '2px',
                                    background: 'linear-gradient(90deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.1) 100%)',
                                }
                            }}
                        >
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Typography variant="h2" sx={{ fontSize: '1.2rem' }}>
                                        🏢
                                    </Typography>
                                    <Typography variant="body1" fontWeight="600" sx={{ fontSize: '1.1rem' }}>
                                        {editMode ? 'Edit Client' : 'Add Client'}
                                    </Typography>
                                </Box>
                                <IconButton
                                    onClick={handleCloseModal}
                                    sx={{
                                        color: 'white',
                                        '&:hover': {
                                            bgcolor: 'rgba(255,255,255,0.1)',
                                            transform: 'rotate(90deg)'
                                        },
                                        transition: 'all 0.2s ease-in-out'
                                    }}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                        </Box>

                        {/* Modal Content */}
                        <Box sx={{
                            p: 3,
                            maxHeight: 'calc(85vh - 160px)', // Adjusted for header and footer
                            overflowY: 'auto',
                            '&::-webkit-scrollbar': {
                                width: '6px',
                            },
                            '&::-webkit-scrollbar-track': {
                                background: '#f1f1f1',
                                borderRadius: '3px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                background: '#c1c1c1',
                                borderRadius: '3px',
                            },
                        }}>

                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth>
                                        <TextField
                                            label="Client Code"
                                            type="text"
                                            value={formData.client_code}
                                            onChange={(e) => handleInputChange('client_code', e.target.value)}
                                            required
                                            size="small"
                                            variant="outlined"
                                            disabled={editMode}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 2,
                                                    backgroundColor: 'rgba(10, 0, 104, 0.02)',
                                                    '&:hover fieldset': {
                                                        borderColor: '#cc4049',
                                                        borderWidth: '2px',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: '#0a0068',
                                                        borderWidth: '2px',
                                                        boxShadow: '0 0 0 3px rgba(10, 0, 104, 0.1)',
                                                    },
                                                },
                                                '& .MuiInputLabel-root': {
                                                    '&.Mui-focused': {
                                                        color: '#0a0068',
                                                        fontWeight: 600,
                                                    },
                                                },
                                            }}
                                        />
                                    </FormControl>
                                </Grid>
                                
                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Status</InputLabel>
                                        <Select
                                            value={formData.status}
                                            onChange={(e) => handleInputChange('status', e.target.value)}
                                            label="Status"
                                            sx={{
                                                borderRadius: 2,
                                                backgroundColor: 'rgba(10, 0, 104, 0.02)',
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#cc4049',
                                                    borderWidth: '2px',
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#0a0068',
                                                    borderWidth: '2px',
                                                    boxShadow: '0 0 0 3px rgba(10, 0, 104, 0.1)',
                                                },
                                            }}
                                        >
                                            <MenuItem value="Active">Active</MenuItem>
                                            <MenuItem value="Inactive">Inactive</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* Enhanced Image Upload Section */}
                                <Grid item xs={12}>
                                    <Box>
                                        <Typography
                                            variant="subtitle1"
                                            gutterBottom
                                            sx={{
                                                fontWeight: 600,
                                                color: 'text.primary',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                mb: 2
                                            }}
                                        >
                                            <BusinessIcon sx={{ fontSize: '1.1rem' }} />
                                            Company Logo
                                        </Typography>

                                        <input
                                            accept="image/*"
                                            style={{ display: 'none' }}
                                            id="logo-upload"
                                            type="file"
                                            onChange={handleImageChange}
                                        />
                                        <label htmlFor="logo-upload">
                                            <Button
                                                variant="outlined"
                                                component="span"
                                                startIcon={<CloudUploadIcon />}
                                                fullWidth
                                                size="small"
                                                sx={{
                                                    textTransform: "capitalize",
                                                    borderColor: "#cc4049",
                                                    color: "#cc4049",
                                                    borderRadius: 2,
                                                    py: 1,
                                                    borderStyle: 'dashed',
                                                    borderWidth: 2,
                                                    fontSize: '0.8rem',
                                                    backgroundColor: 'rgba(204, 64, 73, 0.05)',
                                                    "&:hover": {
                                                        borderColor: "#0a0068",
                                                        color: "#0a0068",
                                                        bgcolor: "rgba(10, 0, 104, 0.05)",
                                                        borderStyle: 'dashed',
                                                        borderWidth: 2,
                                                        transform: 'translateY(-1px)',
                                                    },
                                                    transition: 'all 0.2s ease-in-out',
                                                }}
                                            >
                                                {imagePreview ? 'Change Image' : 'Choose Company Logo'}
                                            </Button>
                                        </label>

                                        {/* Enhanced Image Preview */}
                                        {imagePreview && (
                                            <Fade in={true}>
                                                <Box mt={2}>
                                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                                        <Typography variant="caption" color="text.primary" fontWeight={600} sx={{ fontSize: '0.8rem' }}>
                                                            Preview:
                                                        </Typography>
                                                    </Box>
                                                    <Paper
                                                        elevation={2}
                                                        sx={{
                                                            p: 1.5,
                                                            textAlign: "center",
                                                            bgcolor: "#fafafa",
                                                            borderRadius: 2,
                                                            border: '1px solid #e0e0e0'
                                                        }}
                                                    >
                                                        <img
                                                            src={imagePreview}
                                                            alt="Company Logo Preview"
                                                            style={{
                                                                maxWidth: "100%",
                                                                maxHeight: "120px",
                                                                objectFit: "contain",
                                                                borderRadius: '6px'
                                                            }}
                                                        />
                                                    </Paper>
                                                </Box>
                                            </Fade>
                                        )}
                                    </Box>
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography
                                        variant="subtitle1"
                                        gutterBottom
                                        sx={{
                                            fontWeight: 600,
                                            color: 'text.primary',
                                            mb: 2
                                        }}
                                    >
                                        Description
                                    </Typography>
                                    <DraftEditor
                                        handleDescription={(content) => handleInputChange('description', content)}
                                        description={formData.description}
                                        clearEditor={!formData.description}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography
                                        variant="subtitle1"
                                        gutterBottom
                                        sx={{
                                            fontWeight: 600,
                                            color: 'text.primary',
                                            mb: 1
                                        }}
                                    >
                                        Page Privileges
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        Select which pages this client can access. Leave empty to allow all pages.
                                    </Typography>
                                
                                    <Paper 
                                        variant="outlined"
                                        sx={{ 
                                            maxHeight: 200, 
                                            overflow: 'auto', 
                                            p: 2, 
                                            borderRadius: 2,
                                            bgcolor: 'grey.50',
                                            border: '1px solid #e0e0e0'
                                        }}
                                >
                                    <Grid container spacing={1}>
                                        {availablePages.map((page) => (
                                            <Grid item xs={12} sm={6} key={page.value}>
                                                <Box 
                                                    display="flex" 
                                                    alignItems="center" 
                                                    sx={{
                                                        p: 1,
                                                        borderRadius: 1,
                                                        '&:hover': { bgcolor: 'background.paper' }
                                                    }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        id={`page-${page.value}`}
                                                        checked={formData.page_privileges.includes(page.value)}
                                                        onChange={() => handlePagePrivilegeToggle(page.value)}
                                                        style={{ 
                                                            marginRight: 8,
                                                            accentColor: '#0a0068'
                                                        }}
                                                    />
                                                    <label 
                                                        htmlFor={`page-${page.value}`}
                                                        style={{ 
                                                            cursor: 'pointer',
                                                            fontSize: '0.9rem'
                                                        }}
                                                    >
                                                        {page.label}
                                                    </label>
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>
                                    </Paper>

                                    {formData.page_privileges.length > 0 && (
                                        <Box mt={2}>
                                            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                                                Selected Pages ({formData.page_privileges.length}):
                                            </Typography>
                                        <Box display="flex" flexWrap="wrap" gap={1}>
                                            {formData.page_privileges.map((privilege) => {
                                                const page = availablePages.find(p => p.value === privilege);
                                                return (
                                                    <Chip
                                                        key={privilege}
                                                        label={page?.label || privilege}
                                                        size="small"
                                                        onDelete={() => handlePagePrivilegeToggle(privilege)}
                                                        color="primary"
                                                        variant="outlined"
                                                        sx={{ borderRadius: 2 }}
                                                    />
                                                );
                                            })}
                                            </Box>
                                        </Box>
                                    )}
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Sticky Footer with Action Buttons */}
                        <Box sx={{
                            p: 3,
                            borderTop: '1px solid #e0e0e0',
                            bgcolor: '#fafafa',
                            borderRadius: '0 0 12px 12px'
                        }}>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        onClick={handleCloseModal}
                                        size="small"
                                        sx={{
                                            textTransform: "capitalize",
                                            borderColor: "#cc4049",
                                            color: "#cc4049",
                                            borderRadius: 2,
                                            py: 1,
                                            fontWeight: 600,
                                            fontSize: '0.8rem',
                                            backgroundColor: 'rgba(204, 64, 73, 0.05)',
                                            "&:hover": {
                                                borderColor: "#0a0068",
                                                color: "#0a0068",
                                                bgcolor: "rgba(10, 0, 104, 0.05)",
                                                transform: 'translateY(-1px)',
                                            },
                                            transition: 'all 0.2s ease-in-out',
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </Grid>
                                <Grid item xs={6}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        onClick={handleSubmit}
                                        disabled={loading}
                                        startIcon={loading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : null}
                                        size="small"
                                        sx={{
                                            textTransform: "capitalize",
                                            background: 'linear-gradient(135deg, #0a0068 0%, #cc4049 100%)',
                                            color: "#ffffff",
                                            borderRadius: 2,
                                            py: 1,
                                            fontWeight: 600,
                                            fontSize: '0.8rem',
                                            boxShadow: '0 2px 8px rgba(10, 0, 104, 0.3)',
                                            "&:hover": {
                                                background: 'linear-gradient(135deg, #0c0082 0%, #d63550 100%)',
                                                boxShadow: '0 4px 12px rgba(10, 0, 104, 0.4)',
                                                transform: 'translateY(-1px)',
                                            },
                                            transition: 'all 0.2s ease-in-out',
                                        }}
                                    >
                                        {editMode ? 'Update' : 'Save Changes'}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Box>
                    </Paper>
                </Fade>
            </Modal>

            <Snackbar
                open={alert.open}
                autoHideDuration={6000}
                onClose={handleCloseAlert}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseAlert} severity={alert.severity}>
                    {alert.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ClientManagement;